import { EXTERNAL_CHANNEL_LABEL, MAX_BADGE_LABEL_LENGTH } from './ExternalChannelBadge.constants';

// Words dropped when acronym-izing a company name: connectors ("American Financial Resources" -> "AFR")
// and legal-entity suffixes ("..., LLC" must not become "AFRL").
const NOISE_WORDS = new Set([
  'of',
  'and',
  'the',
  'for',
  '&',
  'llc',
  'inc',
  'corp',
  'co',
  'ltd',
  'lp',
  'llp',
  'pllc',
]);

// Strip surrounding punctuation (commas, periods) so "Resources," contributes "R", not ",".
const stripPunctuation = word => word.replace(/[^\p{L}\p{N}&]/gu, '');

// Turn a lender company name into a compact badge label the way a person abbreviates it:
// multi-word names collapse to their significant-word initials (AFR); a single short word is kept as-is;
// anything else is truncated. Falls back to the generic "Broker" when no company name is available.
export const getCompanyBadgeLabel = companyName => {
  const trimmed = (companyName ?? '').trim();

  if (!trimmed) {
    return EXTERNAL_CHANNEL_LABEL;
  }

  const significantWords = trimmed
    .split(/\s+/)
    .map(stripPunctuation)
    .filter(word => word && !NOISE_WORDS.has(word.toLowerCase()));

  if (significantWords.length > 1) {
    return significantWords
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, MAX_BADGE_LABEL_LENGTH);
  }

  const singleWord = significantWords[0] ?? stripPunctuation(trimmed);

  if (!singleWord) {
    return EXTERNAL_CHANNEL_LABEL;
  }

  return singleWord.length > MAX_BADGE_LABEL_LENGTH
    ? singleWord.slice(0, MAX_BADGE_LABEL_LENGTH)
    : singleWord;
};

// The tooltip names the external counterparty's company when known, phrased so a long proper noun reads
// naturally ("... in this thread with American Financial Resources").
export const getExternalChannelTooltip = companyName =>
  companyName
    ? `External conversation — the broker reads and writes in this thread with ${companyName}`
    : 'External conversation — the broker reads and writes in this thread';
